import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import moment from 'moment';
import {
  FileField,
  FileInput,
  FormDataConsumer,
  useGetMany,
  useGetOne,
} from 'react-admin';
import ChatAccordion from '../../../components/ChatAccordion';
import { ContractProductMeasureType, DealType, DealValueType, PaymentType } from '../../../models';

const SummaryItem = ({ label, value }) => (
  <Box sx={{ marginBottom: 2 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">
      {value || '-'}
    </Typography>
  </Box>
);

const SummarySection = ({ title, children, fullWidth = false }) => (
  <Grid item xs={12} md={fullWidth ? 12 : 6}>
    <Paper sx={{ padding: 3, height: '100%' }}>
      <Typography variant="h6" sx={{ marginBottom: 2 }}>
        {title}
      </Typography>
      <Divider sx={{ marginBottom: 2 }} />
      {children}
    </Paper>
  </Grid>
);

export const SummaryStep = ({ isEdit = false }) => {
  return (
    <FormDataConsumer>
      {({ formData }) => {
        const { data: chain } = useGetOne('chains', { id: formData.chainId }, { enabled: !!formData.chainId });
        const { data: company } = useGetOne('companies', { id: formData.companyId }, { enabled: !!formData.companyId });
        const { data: stations } = useGetMany('stations', { ids: formData.stationIds || [] }, { enabled: (formData.stationIds?.length || 0) > 0 });
        const { data: fuels } = useGetMany('fuels', {
          ids: [
            ...(formData.contractFuels?.map(cf => cf.fuelId) || []),
            ...(formData.contractProducts?.map(cp => cp.fuelId) || [])
          ].filter(Boolean)
        });

        const getFuelName = (fuelId) => fuels?.find(f => f.id === fuelId)?.name || fuelId;

        const formatCurrency = (value) => {
          return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }).format(value || 0);
        };

        const formatDealValue = (value, type) => {
          if (type === DealValueType.cents || type === DealValueType.prefixed) {
            return formatCurrency(value);
          } else if (type === DealValueType.percentage) {
            return `${value || 0}%`;
          }
          return value;
        };

        return (
          <Box sx={{ width: '100%' }}>
            <Typography variant="h5" sx={{ marginBottom: 3 }}>
              Resumo do Contrato
            </Typography>

            <Grid container spacing={3}>
              <SummarySection title="Informações Básicas">
                <SummaryItem label="Rede" value={chain?.name} />
                <SummaryItem label="Nome do Contrato" value={formData.name} />
                <SummaryItem
                  label="Período de Validade"
                  value={formData.startDate && formData.endDate
                    ? `${moment(formData.startDate).format('DD/MM/YYYY')} até ${moment(formData.endDate).format('DD/MM/YYYY')}`
                    : '-'
                  }
                />
                <SummaryItem label="Grupo de Transportadora" value={company?.name} />
                <SummaryItem
                  label="Postos"
                  value={stations?.map(s => s.name).join(', ')}
                />
              </SummarySection>

              <SummarySection title="Regras">
                <SummaryItem
                  label="Válido para agregados"
                  value={formData.isForAggregated ? 'Sim' : 'Não'}
                />
                <SummaryItem
                  label="Compartilhar histórico de preço"
                  value={formData.isPriceLogEnabled ? 'Sim' : 'Não'}
                />
                {formData.isForAggregated && (
                  <SummaryItem
                    label="Compartilhar histórico de preço com Agregado"
                    value={formData.isPriceLogForAggregateEnabled ? 'Sim' : 'Não'}
                  />
                )}
                <SummaryItem
                  label="Período de promoção (dias)"
                  value={formData.dealPeriod}
                />
                {formData.billingDay && formData.billingDay.length > 0 && (
                  <>
                    <Divider sx={{ marginY: 2 }} />
                    <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
                      Faturamento
                    </Typography>
                    <SummaryItem
                      label="Dias de Faturamento"
                      value={formData.billingDay?.join(', ')}
                    />
                    <SummaryItem
                      label="Prazo de Vencimento (dias)"
                      value={formData.dueDate}
                    />
                    <SummaryItem
                      label="Forma de Pagamento"
                      value={formData.paymentType ? PaymentType.translateType(formData.paymentType) : '-'}
                    />
                  </>
                )}
              </SummarySection>

              <SummarySection title="Acordos e faturamentos" fullWidth>
                {formData.contractFuels && formData.contractFuels.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ marginBottom: 2 }}>
                    Acordos de Preço
                  </Typography>
                  {formData.contractFuels.map((fuel, index) => (
                    <Box key={index} sx={{ marginBottom: 2, paddingLeft: 2, borderLeft: '3px solid #1976d2' }}>
                      <Typography variant="body2">
                        <strong>{getFuelName(fuel.fuelId)}</strong>
                      </Typography>
                      <Typography variant="caption" display="block">
                        Tipo: {DealValueType.translateStatus(fuel.dealValueType)} | Valor: {formatDealValue(fuel.dealValue, fuel.dealValueType)} |
                        Mínimo: {fuel.dealMinValue} litros
                        {fuel.dealType && ` | ${DealType.translateType(fuel.dealType)}`}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}

                {formData.contractProducts && formData.contractProducts.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ marginBottom: 2, marginTop: 3 }}>
                    Acordos de Produtos
                  </Typography>
                  {formData.contractProducts.map((product, index) => (
                    <Box key={index} sx={{ marginBottom: 2, paddingLeft: 2, borderLeft: '3px solid #2e7d32' }}>
                      <Typography variant="body2">
                        <strong>{getFuelName(product.fuelId)}</strong>
                      </Typography>
                      <Typography variant="caption" display="block">
                        Medida: {ContractProductMeasureType.translateType(product.measureType)}
                        {product.amount && ` | Capacidade: ${product.amount} litros`}
                        {product.dealValue !== undefined && ` | Valor: ${formatCurrency(product.dealValue)}`}
                        {product.dealMinValue !== undefined && ` | Mínimo: ${product.dealMinValue} litros`}
                        {product.dealType && ` | ${DealType.translateType(product.dealType)}`}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}

                {(!formData.contractFuels || formData.contractFuels.length === 0) &&
                 (!formData.contractProducts || formData.contractProducts.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum acordo configurado
                  </Typography>
                )}
              </SummarySection>

              <SummarySection title="Arquivos e Comunicação" fullWidth>
                <FileInput
                  source={isEdit ? "filePath" : "files"}
                  label="Arquivos"
                  multiple
                  format={isEdit ? (values => {
                    return values ? values.map(value => ({ src: value, title: typeof value === 'string' ? value.split('/').slice(-1) : value.title })) : null;
                  }) : undefined}
                  parse={isEdit ? ((values) => {
                    return values ? values.map(value => value instanceof File ? { rawFile: value, title: value.name } : value.src) : null;
                  }) : undefined}
                >
                  <FileField source="src" title="title" />
                </FileInput>

                <ChatAccordion
                  style={{ marginTop: '40px', minWidth: '100%' }}
                  companyId={formData.companyId}
                  disabledSelectOrigin
                />
              </SummarySection>
            </Grid>
          </Box>
        );
      }}
    </FormDataConsumer>
  );
};
